import { z } from "zod/v4";

import { ExpressionNode } from "../../../dtos/audiences";
import { CartTargetingSchema } from "../../../dtos/cart-targeting";
import { PostPurchaseUpsellVariablesSchema } from "../../../dtos/checkout-blocks";
import {
  ExperienceStatusList,
  ExperienceTypeList,
} from "../../../dtos/experiences";
import { OrganizationDtoSchema } from "../../../dtos/organizations";
import { UpsellProductRulesActionSchema } from "../../../dtos/upsell-product-rules";
import { StoreBenchmarksResponseBodySchema } from "../../analytics/store-benchmarks/store-benchmarks";
import { StyleGuideSchema } from "../../analytics/store-style-guide/store-style-guide";
import { PageReadSchema } from "../shopify/page-read";
import { StoreIndustryClassificationSchema } from "../store-industry/store-industry";
import {
  AuditFunnelSchema,
  AuditLandingPagesSchema,
  AuditLandingSalesSchema,
  AuditPixelLandingPagesSchema,
  CatalogProductSchema,
  ChannelSalesSchema,
  CollectionListingSchema,
  CountryOrdersSchema,
  LiveDiscountSchema,
  LiveThemeSchema,
  OrderCountSchema,
  OrderValueSampleSchema,
  PricedProductSchema,
  ProductIdentitySchema,
  ProductPerformanceSchema,
  RepeatPurchaseSchema,
  ShippingZoneSchema,
  StoreFactsSchema,
  StoreSearchMatchSchema,
  StoreThemeSchema,
  ThemeFilesSchema,
  UpsellProductSchema,
} from "../shopify/shopify";

/**
 * One answer, or the reason there isn't one.
 *
 * **A result reports its own failure rather than failing the call.** The audit
 * reads a store from three sources and each fails independently: Shopify can be
 * slow while our database is fine, a render can time out while both are
 * healthy. A node whose own read is missing says so; every other node carries
 * on.
 *
 * `fetchedAt` because a caller cannot otherwise tell how old what it is showing
 * is, and `cached` because the difference between a stored answer and a fresh
 * one is the whole point and should be visible in a log without instrumenting
 * anything.
 *
 * **Generic over the literal, not just the payload.** `type: string` would infer
 * every `z.literal(type)` as `ZodLiteral<string>`, so every member of the union
 * would carry the same `type` and nothing could discriminate between them — a
 * caller asking for `catalog` would get a value typed as every payload at once.
 */
/**
 * Every post-purchase experience the merchant built: one with a post-purchase
 * block in any state, or of the post-purchase type. Per arm, the block's own
 * settings, so a past test can be read field by field.
 */
export const AuditPostPurchaseTestsSchema = z.array(
  z.object({
    experienceId: z.string(),
    name: z.string().nullable(),
    category: z.enum(["experiment", "personalization"]),
    status: z.enum(ExperienceStatusList),
    /** When it first started, in milliseconds. Null when it never did. */
    firstStartedAt: z.number().nullable(),
    /**
     * True when its audience has live filters, so it reaches some buyers.
     * The audience's `enabled` flag is ignored: it only switches off the
     * currency lock, and the filters apply either way.
     */
    audienceFiltered: z.boolean(),
    arms: z.array(
      z.object({
        isControl: z.boolean(),
        /** Null for an arm with no offer. */
        block: z
          .object({
            maxProductLimit: z.number().nullable(),
            offerTimeout: z.number().nullable(),
            discountType: z.string().nullable(),
            discountValue: z.number().nullable(),
            countdownTimerEnabled: z.boolean(),
            /**
             * The rest of today's offer, so it can be a test's control. Absent
             * from a backend older than this read.
             */
            settings: z
              .object({
                excludeItemsInCart: z.boolean().nullable(),
                excludeOutOfStock: z.boolean().nullable(),
                compareAtPriceDisplay: z.string().nullable(),
                descriptionEnabled: z.boolean().nullable(),
                timerCheckout:
                  PostPurchaseUpsellVariablesSchema.shape.timerCheckout.nullable(),
                /** Which products it offers, for which carts, in priority order. Null when one could not be read. */
                rules: z
                  .array(
                    z.object({
                      priority: z.number(),
                      cart: CartTargetingSchema.shape.expression,
                      action: UpsellProductRulesActionSchema,
                    })
                  )
                  .nullable(),
              })
              .optional(),
          })
          .nullable(),
      })
    ),
  })
);

/**
 * What the merchant has already built in Intelligems, as the audit reads it.
 *
 * Counts and a short list rather than every experience: a store with 412 of
 * them came to 3.7MB as list records, a Lambda answers at most 6MB, and the
 * audit only ever counted them or picked out the running ones.
 */
export const AuditExistingTestsSchema = z.object({
  /** Experiences not deleted, per type and status. */
  experiences: z.array(
    z.object({
      type: z.enum(ExperienceTypeList),
      status: z.enum(ExperienceStatusList),
      count: z.number(),
    })
  ),
  /**
   * Checkout blocks on variations and experiences not deleted, per block type
   * and the experience's status. One per block, so a block in each arm counts
   * in each.
   */
  checkoutBlocks: z.array(
    z.object({
      blockType: z.string(),
      status: z.enum(ExperienceStatusList),
      count: z.number(),
    })
  ),
  /**
   * Offers on versions of experiences not deleted or archived, per the
   * experience's status. One per version that carries one.
   */
  offers: z.array(
    z.object({
      status: z.enum(ExperienceStatusList),
      count: z.number(),
    })
  ),
  /** Experiences started or paused, of any type, with their page targeting. */
  running: z.array(
    z.object({
      name: z.string().nullable(),
      type: z.enum(ExperienceTypeList),
      status: z.enum(["started", "paused"]),
      /** One expression per targeting rule, in the plugin's postfix order. */
      pageTargeting: z.array(z.array(ExpressionNode)),
      /** Shopify product ids a price test prices or an offer covers. Empty for other types. */
      productIds: z.array(z.string()),
      /**
       * Where a URL test sends its visitors from: the page it runs on. Theme
       * and template tests are left out. Optional because reads cached before
       * it have none.
       */
      redirectOrigins: z
        .array(z.object({ url: z.string(), filter: z.string() }))
        .optional(),
    })
  ),
});

const result = <N extends string, T extends z.ZodTypeAny>(type: N, data: T) =>
  z.discriminatedUnion("status", [
    z.object({
      type: z.literal(type),
      status: z.literal("ok"),
      data,
      fetchedAt: z.string(),
      cached: z.boolean(),
    }),
    z.object({
      type: z.literal(type),
      status: z.literal("failed"),
      reason: z.string(),
      /** `denied` is a permission the store has not granted; `error` is anything else. */
      cause: z.enum(["denied", "error"]).optional(),
      /** The Shopify scope a denied read needs, when Shopify names it. */
      scope: z.string().optional(),
      at: z.string().optional(),
      /** Failures in a row. */
      attempts: z.number().optional(),
      /** Until then the read is answered with this failure rather than made. */
      retryAt: z.string().optional(),
    }),
  ]);

/**
 * The store's product variants in the app's cost table, and how many carry a
 * cost. The costs come from Shopify or from a file the merchant uploaded, and
 * they are what profit per visitor is measured on. A store the table has never
 * seen has zero variants.
 */
export const ProductCostCoverageSchema = z.object({
  variants: z.number(),
  costed: z.number(),
  /** Of the variants asked about, those that carry a cost. */
  costedVariantIds: z.array(z.string()).optional(),
  /**
   * Of the variants asked about, those with any row in our product table,
   * costed or not. None means the table has not synced them, not that they
   * carry no cost.
   */
  listed: z.number().optional(),
});

/**
 * Who runs the store, how big their team is, and the apps it runs that bear on testing.
 *
 * Each part takes the best source that answers: the merchant's own onboarding
 * answers first, then our HubSpot records, which include Store Leads' crawl.
 */
export const AuditStoreProfileSchema = z.object({
  /** The first person on the store who is not Intelligems staff. */
  contact: z
    .object({
      name: z.string(),
      /** Their onboarding role, else their HubSpot job title. */
      role: z.string().nullable(),
    })
    .nullable(),
  team: z
    .discriminatedUnion("type", [
      /** The onboarding answer, verbatim. */
      z.object({ type: z.literal("answered"), answer: z.string() }),
      /** An employee count from HubSpot or Store Leads. An estimate. */
      z.object({ type: z.literal("estimated"), employees: z.number() }),
    ])
    .nullable(),
  /**
   * The apps Store Leads found on the storefront that bear on testing, by kind
   * and in that order. Every other app is left out. Empty when unknown.
   */
  stack: z.array(
    z.object({
      kind: z.enum([
        "testing",
        "subscriptions",
        "upsells",
        "bundles",
        "shippingProtection",
        "pageBuilders",
      ]),
      name: z.string(),
    })
  ),
});

export const AuditContextResultSchema = z.union([
  result("entitlements", OrganizationDtoSchema),
  result("existingTests", AuditExistingTestsSchema),
  result("postPurchaseTests", AuditPostPurchaseTestsSchema),
  result("benchmarks", StoreBenchmarksResponseBodySchema.nullable()),
  result("storeProfile", AuditStoreProfileSchema),
  result("productCosts", ProductCostCoverageSchema.nullable()),
  result(
    "industry",
    z.object({
      /** Null when unclassified, or classified as nothing. */
      classification: StoreIndustryClassificationSchema.nullable(),
      /** The classification is old enough to redo; it is still served meanwhile. */
      stale: z.boolean(),
      /** The last classification that failed, while it waits to be tried again. */
      failure: z.object({ reason: z.string(), retryAt: z.string() }).optional(),
    })
  ),
  result(
    "styleGuide",
    z.object({
      url: z.string(),
      /**
       * Null when the page loaded but our extractor was not on it. A different
       * failure from a store whose styling we could not read.
       */
      styleGuide: StyleGuideSchema.nullable(),
    })
  ),
  result("storeFacts", StoreFactsSchema),
  result("shippingZones", z.array(ShippingZoneSchema)),
  result("liveDiscounts", z.array(LiveDiscountSchema)),
  result("catalog", z.array(CatalogProductSchema)),
  result("liveTheme", LiveThemeSchema.nullable()),
  result("orderCount", OrderCountSchema),
  result("orderValues", OrderValueSampleSchema),
  result("funnel", AuditFunnelSchema),
  result("salesByProduct", z.array(ProductPerformanceSchema)),
  result("repeatPurchases", z.array(RepeatPurchaseSchema)),
  result("salesByChannel", z.array(ChannelSalesSchema)),
  result("salesByCountry", z.array(CountryOrdersSchema)),
  result(
    "salesTotals",
    z.object({ netSales: z.number(), orders: z.number() }).nullable()
  ),
  result("landingPages", AuditLandingPagesSchema),
  result("salesByLandingPage", AuditLandingSalesSchema),
  result("pixelLandingPages", AuditPixelLandingPagesSchema),
  result("discounts", z.number().nullable()),
  result("pricedVariants", z.array(PricedProductSchema)),
  /** Keyed by the id asked for. An id that did not resolve is absent. */
  result("productsForIds", z.record(z.string(), ProductIdentitySchema)),
  result("upsellProducts", z.array(UpsellProductSchema)),
  result("collectionMembers", CollectionListingSchema),
  result("postPurchasePage", z.object({ intelligemsSelected: z.boolean() })),
  result("pageElements", PageReadSchema.nullable()),
  result(
    "themeByName",
    z.object({
      named: StoreThemeSchema.nullable(),
      live: StoreThemeSchema.nullable(),
    })
  ),
  result("themeFiles", ThemeFilesSchema.nullable()),
  result("storeSearch", z.array(StoreSearchMatchSchema)),
  result("themeTemplates", z.array(z.string())),
]);

export const AuditContextResponseBodySchema = z.object({
  /** One per request, in the order they were asked for. */
  results: z.array(AuditContextResultSchema),
});

export type AuditContextResultType = z.infer<typeof AuditContextResultSchema>;
export type ProductCostCoverageType = z.infer<typeof ProductCostCoverageSchema>;
export type AuditExistingTestsType = z.infer<typeof AuditExistingTestsSchema>;
export type AuditPostPurchaseTestsType = z.infer<
  typeof AuditPostPurchaseTestsSchema
>;
export type AuditContextResponseBodyType = z.infer<
  typeof AuditContextResponseBodySchema
>;
export type AuditStoreProfileType = z.infer<typeof AuditStoreProfileSchema>;
